import type { ComponentProps } from 'react';

import { Button } from '@/components/ui/button';
import { CTA_BASE, CTA_ENABLED } from '@/components/ui/hotels/hotel-cta';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

/** A card's action at the chat CTA's size, with its own busy state instead of a card swap. */
export function ActionButton({
  busy,
  tone = 'accent',
  children,
  className,
  ...props
}: Omit<ComponentProps<typeof Button>, 'variant' | 'size'> & {
  busy?: boolean;
  tone?: 'accent' | 'destructive' | 'outline';
}) {
  return (
    <Button
      {...props}
      variant={tone === 'accent' ? 'default' : tone}
      size="lg"
      disabled={busy || props.disabled}
      aria-busy={busy}
      // One geometry for every tone, and only colours transition: `Button` animates `all`, so
      // swapping tones on the same button would otherwise tween its padding.
      className={cn(
        'w-full px-3 transition-colors has-[>svg]:px-3',
        // The reservation card's CTA, so every action in one thread shares the tenant's button. Its
        // hover is a fade only: without restating the background here, `Button`'s own
        // `hover:bg-primary/90` survives the merge and turns the CTA the primary colour on hover.
        tone === 'accent' && [
          CTA_BASE,
          CTA_ENABLED,
          'shadow-none hover:bg-(--hotel-card-cta-bg,var(--brand-accent,var(--primary)))',
        ],
        // Neutral, so stepping back never borrows the brand tint of the action beside it.
        tone === 'outline' && 'hover:bg-muted hover:text-foreground dark:hover:bg-muted',
        className,
      )}
    >
      {busy && <Spinner size="sm" />}
      {children}
    </Button>
  );
}

/** The step back beside a card's action. It waits while that action runs. */
export function CancelActionButton({
  label,
  onCancel,
  disabled,
}: {
  label: string;
  onCancel?: () => void;
  disabled?: boolean;
}) {
  return (
    <ActionButton
      type="button"
      tone="outline"
      onClick={onCancel}
      disabled={disabled}
      className="flex-1"
    >
      {label}
    </ActionButton>
  );
}
